import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { GraduationCap, Users as UsersIcon, BookOpen, Shield } from 'lucide-react';

const RoleSelection = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(false);

  const roles = [
    {
      id: 'student',
      name: 'Student',
      icon: GraduationCap,
      description: 'I want to learn and attend classes',
      color: 'from-blue-400 to-cyan-500'
    },
    {
      id: 'parent',
      name: 'Parent',
      icon: UsersIcon,
      description: 'I want to manage my children\'s education',
      color: 'from-purple-400 to-pink-500'
    },
    {
      id: 'tutor',
      name: 'Tutor',
      icon: BookOpen,
      description: 'I want to teach and manage students',
      color: 'from-green-400 to-emerald-500'
    }
  ];

  const handleRoleSelect = async () => {
    if (!selectedRole) return;
    
    setLoading(true);
    
    try {
      // Get the current user from localStorage
      const savedUser = localStorage.getItem('fourq_user');
      if (savedUser) {
        const userData = JSON.parse(savedUser);
        userData.role = selectedRole;
        localStorage.setItem('fourq_user', JSON.stringify(userData));
      }
      
      // Navigate to dashboard
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Error setting role:', error);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-600 via-blue-600 to-teal-600 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Select Your Role</h1>
          <p className="text-gray-600">Choose how you want to use FourQ Learn</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {roles.map((role) => {
            const Icon = role.icon;
            return (
              <motion.div
                key={role.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedRole(role.id)}
                className={`
                  cursor-pointer rounded-xl p-6 border-2 transition-all
                  ${selectedRole === role.id
                    ? 'border-cyan-500 bg-cyan-50 shadow-lg'
                    : 'border-gray-200 hover:border-gray-300'
                  }
                `}
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${role.color} rounded-xl flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{role.name}</h3>
                <p className="text-sm text-gray-600">{role.description}</p>
              </motion.div>
            );
          })}
        </div>

        <Button
          onClick={handleRoleSelect}
          disabled={!selectedRole || loading}
          className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white py-3 text-lg"
        >
          {loading ? 'Loading...' : 'Continue to Dashboard'}
        </Button>
      </motion.div>
    </div>
  );
};

export default RoleSelection;
